// AntD table related stuff

import React from 'react';
import { Input, Radio } from 'antd';
import moment from 'moment';
import globalStyles from '../styles/global';
import { mrg } from '../common/util';
import Highlighter from 'react-highlight-words';
import op from 'object-path';

const styles = {
  column: {
    fontSize: globalStyles.global.baseline * 1.2,
    fontWeight: 100,
    padding: 0,
    width: 'auto',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    wordBreak: 'break-all'
  },
  radio: {
    container: {
      ...globalStyles.layout.flexHorizontal,
      ...globalStyles.layout.flexStart,
      ...globalStyles.layout.alignCenter
    },
    button: {
      height: 'auto',
      borderRadius: 0,
      display: 'block',
      marginBottom: globalStyles.global.baseline / 4,
      textAlign: 'center',
      lineHeight: '30px',
      padding: '0 9px'
    },
    group: {},
    title: {
      paddingBottom: globalStyles.global.baseline * 0.5,
      marginBottom: globalStyles.global.baseline / 2
    }
  }
};

const EditableCell = ({
  editable,
  value,
  onChange,
  style = {},
  highlight = ''
}) => (
  <div>
    {editable ? (
      <Input.TextArea
        style={style}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    ) : highlight && typeof value === 'string' ? (
      <Highlighter
        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
        searchWords={[highlight]}
        autoEscape
        textToHighlight={value.toString()}
      />
    ) : (
      value
    )}
  </div>
);
const EditableRadioCell = ({
  editable,
  options,
  value,
  recordKey,
  onChange
}) => (
  <div style={styles.radio.container}>
    <Radio.Group
      size="large"
      style={mrg([styles.radio.group, !editable && { pointerEvents: 'none' }])}
      buttonStyle={editable ? 'dashed' : 'solid'}
      onChange={e => (editable ? onChange(e.target.value) : null)}
      value={value}
    >
      {Object.keys(options).map((key, index) => {
        let disabled;
        let label;
        if (typeof options[key] === 'string') {
          label = options[key];
          disabled = false;
        } else {
          label = options[key].label;
          disabled = options[key].disabled;
        }
        return (
          <Radio.Button
            disabled={disabled}
            key={key}
            value={key}
            style={styles.radio.button}
          >
            {label}
          </Radio.Button>
        );
      })}
    </Radio.Group>
  </div>
);

const renderEditableColumn = (
  obj,
  record,
  column,
  onChange,
  isRadio = false,
  options = [],
  highlight = ''
) => {
  if (isRadio) {
    return (
      <EditableRadioCell
        editable={record.editable}
        options={options}
        recordKey={record.nif}
        value={obj}
        onChange={value => onChange(value, record.nif, column, isRadio)}
      />
    );
  }
  return (
    <EditableCell
      editable={record.editable}
      value={obj}
      onChange={value => onChange(value, record.nif, column)}
      highlight={highlight}
    />
  );
};

const renderColumn = (colSpan, styles = {}, translations = null) => text => {
  const result = text ? (translations ? translations[text] : text) : '-';
  return {
    props: { colSpan: colSpan },
    children: <span style={mrg([styles.column, styles])}>{result}</span>
  };
};
const renderDateColumn = (colSpan, format = 'YYYY-MM-DD') => date => {
  return {
    children: <span style={styles.column}>{moment(date).format(format)}</span>
  };
};

const textSorter = fieldName => (a, b) => {
  const aExists = op.get(a, fieldName) && op.get(a, fieldName).localeCompare;
  const bExists = typeof op.get(b, fieldName) === 'string';
  if (!aExists && !bExists) {
    return 0;
  } else if (!aExists) {
    return -1;
  } else if (!bExists) {
    return 1;
  }
  return op.get(a, fieldName).localeCompare(op.get(b, fieldName));
};
const dateSorter = fieldName => (a, b) => {
  const aExists = op.get(a, fieldName) && op.get(a, fieldName).diff;
  const bExists = op.get(b, fieldName) instanceof Date;
  if (!aExists && !bExists) {
    return 0;
  } else if (!aExists) {
    return 1;
  } else if (!bExists) {
    return -1;
  }
  return op.get(a, fieldName).diff(op.get(b, fieldName));
};

const booleanSorter = fieldName => (a, b) => {
  if (op.get(a, fieldName)) return -1;
  if (op.get(b, fieldName)) return 1;
  // if both are false, this should keep the default order:
  return 0;
};

export {
  renderColumn,
  renderDateColumn,
  renderEditableColumn,
  textSorter,
  dateSorter,
  booleanSorter,
  EditableCell,
  EditableRadioCell
};
